import { InvalidUrlError } from '@execonline-inc/url';
import { Maybe } from 'maybeasy';
import { AppyError } from '../../../../../../Appy';
import { MissingLinkError } from '../../../../../../LinkyLinky';
import { Resource } from '../../../../../../Resource/Types';
import { TPlainTextKey } from '../../../../../../Translations';

// How much of the collection a group can enrol in. Licenses gate enrolment,
// not sight: everyone who is invited receives the collection and can browse it.
export type Coverage = 'full' | 'partial' | 'none';

// Whether the organization could put this license behind a course today.
export type LicenseAvailability = 'available' | 'not-yet-started' | 'expired' | 'not-held';

export interface GrantingLicense {
  name: string;
  status: LicenseAvailability;
}

export interface ProgramFamily {
  id: number;
  name: string;
  offeringType: string;
  // A course can sit behind more than one license, best option first.
  grantingLicenses: ReadonlyArray<GrantingLicense>;
}

export interface GroupParticipant {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  // Nothing when the participant holds no active licence at all.
  licenseLabel: Maybe<string>;
  licenseStartOn: Maybe<Date>;
  licenseEndOn: Maybe<Date>;
}

// A group is one distinct set of courses its members cannot enrol in, not one
// set of licenses: people who end up with the same access belong together
// however they got there.
export interface LicenseGroup {
  key: string;
  // The licence the group is headed by. Nothing when its members hold none.
  primaryLicenseLabel: Maybe<string>;
  coverage: Coverage;
  hiddenProgramFamilyIds: ReadonlyArray<number>;
  expiresBeforeCollectionEnd: boolean;
  // The first date someone here loses access while the collection still runs.
  expiresOn: Maybe<Date>;
  participants: ReadonlyArray<GroupParticipant>;
}

// A license that would unlock some of this collection, and how much of it.
export interface CollectionLicenseOption {
  name: string;
  status: LicenseAvailability;
  courseCount: number;
}

export interface CollectionPrograms {
  programFamilies: ReadonlyArray<ProgramFamily>;
  // Nothing when the collection holds no scheduled courses, which is also when
  // no license can be said to expire early.
  lastProgramEndOn: Maybe<Date>;
  licenseOptions: ReadonlyArray<CollectionLicenseOption>;
}

export interface LicenseAlerts {
  collection: CollectionPrograms;
  licenseGroups: ReadonlyArray<LicenseGroup>;
  notFound: ReadonlyArray<string>;
}

export type LicenseAlertsResource = Resource<LicenseAlerts>;

export interface Request {
  learningCollectionId: number;
  userIds: ReadonlyArray<number>;
  groupIds: ReadonlyArray<number>;
  notFoundEmails: ReadonlyArray<string>;
}

export interface Waiting {
  kind: 'waiting';
}

export interface Loading {
  kind: 'loading';
  request: Request;
}

export interface Ready {
  kind: 'ready';
  request: Request;
  licenseAlertsResource: LicenseAlertsResource;
}

export interface Error {
  kind: 'error';
  request: Request;
  message: TPlainTextKey;
}

export type LicenseAlertsState = Waiting | Loading | Ready | Error;

export const waiting = (): Waiting => ({ kind: 'waiting' });

export const loading = (request: Request): Loading => ({ kind: 'loading', request });

export const ready = (request: Request, licenseAlertsResource: LicenseAlertsResource): Ready => ({
  kind: 'ready',
  request,
  licenseAlertsResource,
});

export const error = (request: Request, message: TPlainTextKey): Error => ({
  kind: 'error',
  request,
  message,
});

export type LicenseAlertsError = MissingLinkError | AppyError | InvalidUrlError;
